import React, { createContext, useEffect, ReactNode, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { useLocation } from 'react-router-dom';
import { GET_ALL_USERS_BY_FILTER } from '../graphql/GET_ALL_USERS_BY_FILTER';
import { UserData } from '../types/user';
import { algoliasearch } from 'algoliasearch';

export enum EUserFilterTypes {
  default = 'ALL_USERS',
  search = 'FILTERED_USERS_FROM_QUERY_STRING',
  peopleIamLookingFor = 'PEOPLE_I_AM_LOOKING_FOR_FILTER',
  peopleLookingForMe = 'PEOPLE_LOOKING_FOR_ME_FILTER'
}

export const PAGE_LIMIT = 12;

interface ICreativeListState {
  creatives: UserData[];
  error: any;
  filterType: EUserFilterTypes;
  isLoading: boolean;
  onFetchMore: () => Promise<void>;
  page: number;
  setFilterType: (type: EUserFilterTypes) => void;
  totalCreatives: number;
  selectedUserId: number | null;
  setSelectedUserId: (userId: number | null) => void;
}

const initialState: ICreativeListState = {
  creatives: [],
  error: undefined,
  filterType: EUserFilterTypes.default,
  isLoading: false,
  onFetchMore: async () => {
    console.log('Default onFetchMore');
  },
  page: 1,
  setFilterType: (_: EUserFilterTypes) => {
    console.log('Default setFilterType', _);
  },
  totalCreatives: 0,
  selectedUserId: null,
  setSelectedUserId: (_: number | null) => {
    // Default implementation
  }
};

export const CreativeListContext = createContext<ICreativeListState>(initialState);

interface ICreativeListProviderProps {
  children: ReactNode;
}

const createSearchClient = () => {
  const appId = process.env.REACT_APP_ALGOLIA_APP_ID ?? '';
  const apiKey = process.env.REACT_APP_ALGOLIA_API_KEY ?? '';

  if (appId && apiKey) {
    return algoliasearch(appId, apiKey) as any;
  } else {
    return {
      search: async () => ({
        results: [
          {
            hits: [],
            nbHits: 0,
            page: 0
          }
        ]
      })
    } as any;
  }
};

const searchClient = createSearchClient();

export const CreativeListContextProvider = (
  props: ICreativeListProviderProps
) => {
  const location = useLocation();
  const [creatives, setCreatives] = React.useState<UserData[]>([]);
  const [totalCreatives, setTotalCreatives] = React.useState<number>(0);
  const [pageNum, setPageNum] = React.useState<number>(2);
  const [selectedUserId, setSelectedUserId] = React.useState<number | null>(null);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<any>(null);

  const searchStr = useMemo(() => {
    const urlParams = new URLSearchParams(location.search);
    return urlParams.get('q');
  }, [location.search]);

  const filterTypes = searchStr ? EUserFilterTypes.search : EUserFilterTypes.default;
  const [filterType, setFilterType] = React.useState<EUserFilterTypes>(filterTypes);

  // GraphQL query
  const { loading: graphqlLoading, error: graphqlError, data: graphqlData, fetchMore } = useQuery(
    GET_ALL_USERS_BY_FILTER,
    {
      variables: {
        filterType,
        limit: PAGE_LIMIT,
        page: 1
      }
    }
  );

  const searchWithAlgolia = async (query: string, page = 0) => {
    try {
      setIsLoading(true);
      setError(null);

      const [userResponse, projectResponse] = await Promise.all([
        searchClient.search([
          {
            indexName: 'user',
            query,
            params: {
              hitsPerPage: 100,
              page,
              restrictSearchableAttributes: ['name']
            }
          }
        ]),
        searchClient.search([
          {
            indexName: 'project',
            query,
            params: {
              hitsPerPage: 100,
              restrictSearchableAttributes: ['title']
            }
          }
        ])
      ]);

      const usersByName = userResponse.results?.[0]?.hits || [];
      const projects = projectResponse.results?.[0]?.hits || [];
      const authorEmails = Array.from(new Set(projects.map((p: any) => p.authorEmail).filter(Boolean)));

      let usersByProject: any[] = [];

      if (authorEmails.length > 0) {
        try {
          const userSearchPromises = authorEmails.map(email =>
            searchClient.search([
              {
                indexName: 'user',
                query: email,
                params: {
                  hitsPerPage: 1,
                  restrictSearchableAttributes: ['email']
                }
              }
            ])
          );

          const userResponses = await Promise.all(userSearchPromises);

          usersByProject = userResponses
            .map((response: any) => response.results?.[0]?.hits?.[0])
            .filter(Boolean);
        } catch (err) {
          console.error('Failed to fetch users by email:', err);
        }
      }

      const allUsers = [...usersByName, ...usersByProject];
      const uniqueUsers = Array.from(
        new Map(allUsers.map(user => [user.objectID || user.id, user])).values()
      );

      const totalCount = userResponse.results?.[0]?.nbHits || 0;

      return {
        users: uniqueUsers,
        count: totalCount,
        page: page + 1
      };
    } catch (err) {
      console.error('Algolia search error:', err);
      setError(err);
      return {
        users: [],
        count: 0,
        page: 0
      };
    } finally {
      setIsLoading(false);
    }
  };

  // Effect to handle initial data fetching
  useEffect(() => {
    const fetchInitialData = async () => {
      if (searchStr) {
        setIsLoading(true);

        const searchResults = await searchWithAlgolia(searchStr, 0);

        setCreatives(searchResults.users as any);
        setTotalCreatives(searchResults.count);
        setPageNum(2);
      } else if (graphqlData && graphqlData.getAllUsersByFilter) {
        setCreatives(graphqlData.getAllUsersByFilter.users);
        setTotalCreatives(graphqlData.getAllUsersByFilter.count);
        setPageNum(2);
        setIsLoading(false);
      }
    };
    fetchInitialData();
  }, [searchStr, graphqlData, filterType]);

  const onFetchMore = async (): Promise<void> => {
    const nextPage = pageNum;
    setPageNum(prevNum => prevNum + 1);
    if (searchStr) {
      const searchResults = await searchWithAlgolia(searchStr, nextPage - 1);
      if (searchResults.users.length > 0) {
        setCreatives(prev => [...prev, ...searchResults.users as any]);
      }
    } else {
      const result = await fetchMore({
        variables: {
          limit: PAGE_LIMIT,
          page: nextPage
        }
      });

      if (result.data && result.data.getAllUsersByFilter) {
        setCreatives(prev => [...prev, ...result.data.getAllUsersByFilter.users]);
      }
    }
  };

  const contextValue: ICreativeListState = {
    creatives,
    error: error || graphqlError,
    filterType,
    isLoading: isLoading || graphqlLoading,
    onFetchMore,
    page: pageNum,
    setFilterType,
    totalCreatives,
    selectedUserId,
    setSelectedUserId
  };

  return (
    <CreativeListContext.Provider value={contextValue}>
      {props.children}
    </CreativeListContext.Provider>
  );
};